import { useState } from 'react';
import { Button, Input, Select } from '../../components/ui';
import { useUnclaimedTouches } from './leadActivity';
import { SKIP_LABELS, confirmLabel, planEnrolment } from './leadRoundEnrol';
import {
  ROUND_ONE,
  isWindowUnit,
  maxTouchesFor,
  paceIsPossible,
  roundErrorMessage,
  useRunningRounds,
  useStartRoundsForLeads,
  windowEndOf,
  WINDOW_UNIT_OPTIONS,
} from './leadRounds';
import type { WindowUnit } from './leadRounds';
import { useLeadLoops } from './leadLoop';
import { adoptedBy, defaultStartsOn } from './roundAdoption';
import type { LeadActivity } from './leadActivity';
import { dayLabel, todayIso } from './trackerFormat';
import type { Lead, LeadStage } from './types';

/**
 * Start a round for the leads that are ticked.
 *
 * ── What this replaces ─────────────────────────────────────────────────────
 *
 * On 31 August a bulk enrolment was run FOR Kyu, in SQL, by Claude. It
 * excluded closed leads and not converted ones and put eight live deals into a
 * four-email chase sequence. His answer was not "be more careful next time":
 *
 *   "let's not do this here in cloud. I just want the team to have that
 *    ability to do that."
 *   "we just want the system to have a clear, not confusing mechanism."
 *
 * So the capability belongs to whoever is at the table, and the panel has to
 * be safe when THEY use it — which means saying, before anything is written,
 * exactly who is being enrolled, who is being skipped and why, how much
 * sending it asks for, and when the tasks land.
 *
 * ── Three places the same rules live, on purpose ───────────────────────────
 *
 *   planEnrolment            works out what SHOULD happen, so this can say so
 *   start_rounds_for_leads   decides what DOES happen, in the database
 *   this component           shows the first and reports the second
 *
 * The duplication is deliberate. A tab left open across a stage change carries
 * a stale idea of who is eligible, so the panel's answer is a prediction and
 * the database's is the fact. What lands on screen afterwards is what the
 * database did, not what the panel guessed.
 */
export function StartRoundsForSelected({
  leads,
  selectedIds,
  stages,
  onDone,
  onError,
}: {
  leads: Lead[];
  selectedIds: Set<string>;
  stages: LeadStage[];
  /** Called with what actually happened, and the ids that got a round. */
  onDone: (message: string, started: string[]) => void;
  onError: (message: string) => void;
}) {
  const ids = [...selectedIds];
  const runningQuery = useRunningRounds();
  const loopsQuery = useLeadLoops();
  const unclaimedQuery = useUnclaimedTouches(ids);
  const startRounds = useStartRoundsForLeads();

  const [open, setOpen] = useState(false);
  const [startsOn, setStartsOn] = useState(todayIso());
  const [target, setTarget] = useState(ROUND_ONE.touchTarget);
  const [length, setLength] = useState(ROUND_ONE.windowLength);
  const [unit, setUnit] = useState<WindowUnit>(ROUND_ONE.windowUnit);

  const unclaimed = unclaimedQuery.data ?? new Map<string, string[]>();

  /**
   * The oldest unclaimed email across the whole selection, within 30 days.
   *
   * One date for everybody, because a round has one start date and this panel
   * starts one round each. Taking the OLDEST means nobody's history is thrown
   * away by the default; anyone whose emails predate it can be done singly
   * from their drawer, which is where a per-lead date belongs.
   */
  const suggestedStart = () => {
    const today = todayIso();
    let earliest: string | null = null;
    for (const id of ids) {
      const stamps = unclaimed.get(id) ?? [];
      const day = defaultStartsOn(
        stamps.map(
          (occurredAt) =>
            ({ kind: 'email_sent', occurred_at: occurredAt, round_id: null }) as LeadActivity,
        ),
        today,
      );
      if (day !== today && (earliest === null || day < earliest)) {
        earliest = day;
      }
    }
    return earliest ?? today;
  };

  /** Touches each lead has that a round of THIS shape would claim. */
  const adopted = new Map<string, number>();
  for (const [leadId, stamps] of unclaimed) {
    adopted.set(
      leadId,
      adoptedBy(
        stamps.map(
          (occurredAt) =>
            ({ kind: 'email_sent', occurred_at: occurredAt, round_id: null }) as LeadActivity,
        ),
        { startsOn, windowLength: length, windowUnit: unit },
      ).length,
    );
  }

  const possible = paceIsPossible({ touchTarget: target, windowLength: length, windowUnit: unit });
  const ceiling = maxTouchesFor(length, unit);
  const hasDate = /^\d{4}-\d{2}-\d{2}$/.test(startsOn);

  const plan = planEnrolment(
    leads,
    ids,
    stages,
    runningQuery.data ?? new Map(),
    loopsQuery.data ?? new Map(),
    {
      startsOn: hasDate ? startsOn : todayIso(),
      touchTarget: target,
      windowLength: length,
      windowUnit: unit,
    },
    adopted,
  );

  // Pending, not empty. An empty running-rounds map reads as "nobody is in a
  // round", which is the one wrong answer that leads to a second round being
  // started on a lead that already has one.
  const loading = runningQuery.isPending || loopsQuery.isPending || unclaimedQuery.isPending;

  if (!open) {
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled={loading}
        onClick={() => {
          setStartsOn(suggestedStart());
          setTarget(ROUND_ONE.touchTarget);
          setLength(ROUND_ONE.windowLength);
          setUnit(ROUND_ONE.windowUnit);
          setOpen(true);
        }}
      >
        {loading ? 'Checking rounds…' : 'Start a round'}
      </Button>
    );
  }

  const submit = () => {
    if (!possible || !hasDate || plan.starting.length === 0) {
      return;
    }
    startRounds.mutate(
      {
        // Only the leads the plan would enrol. Sending the whole selection
        // would ask the database to refuse the rest one at a time, and every
        // refusal it reports is a row this panel has already explained.
        leadIds: plan.starting.map((entry) => entry.leadId),
        startsOn,
        touchTarget: target,
        windowLength: length,
        windowUnit: unit,
      },
      {
        onSuccess: (rows) => {
          const started = rows.filter((row) => row.skipped === null);
          const refused = rows.filter((row) => row.skipped !== null);
          setOpen(false);
          // What the DATABASE did, not what the panel predicted. If those
          // differ, the difference is the news.
          onDone(
            refused.length === 0
              ? `Started ${String(started.length)} ${started.length === 1 ? 'round' : 'rounds'}.`
              : `Started ${String(started.length)}. ${String(refused.length)} were refused — the list changed while the panel was open, so check them and try again.`,
            started.map((row) => row.leadId),
          );
        },
        onError: (cause) => {
          onError(roundErrorMessage(cause instanceof Error ? cause.message : ''));
        },
      },
    );
  };

  const nameOf = (leadId: string) => {
    const lead = leads.find((entry) => entry.id === leadId);
    if (lead === undefined) {
      return 'a lead no longer in the list';
    }
    const name = `${lead.first_name} ${lead.last_name}`.trim();
    return name === '' ? lead.email : name;
  };

  return (
    <div className="round-bulk">
      <div className="round-bulk-shape">
        <Input
          label="Starts on"
          type="date"
          value={startsOn}
          onChange={(event) => {
            setStartsOn(event.target.value);
          }}
        />
        <Input
          label="Follow-ups"
          type="number"
          min={1}
          max={ceiling}
          value={String(target)}
          error={
            possible ? undefined : 'More than one a day. Lengthen the window or ask for fewer.'
          }
          onChange={(event) => {
            setTarget(Number(event.target.value));
          }}
        />
        <Input
          label="Window"
          type="number"
          min={1}
          value={String(length)}
          onChange={(event) => {
            setLength(Number(event.target.value));
          }}
        />
        <Select
          label="Counted in"
          value={unit}
          options={WINDOW_UNIT_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={(event) => {
            const next = event.target.value;
            if (isWindowUnit(next)) {
              setUnit(next);
            }
          }}
        />
      </div>

      {/* The load. This is the number that stops somebody putting 133 leads
          into a five-day round — 107 sends a day, which nobody does, so the
          round is unfinishable the moment it starts. */}
      {hasDate ? (
        <p className="round-bulk-load">
          <strong>{String(plan.totalSends)}</strong> emails to send
          {plan.alreadySent > 0
            ? ` — ${String(plan.alreadySent)} already sent count toward these rounds`
            : ''}
          {plan.sendsPerDay > 0 ? `, about ${String(plan.sendsPerDay)} a day` : ''}. Tasks land{' '}
          {dayLabel(windowEndOf(startsOn, length, unit))}.
        </p>
      ) : (
        <p className="round-bulk-load">Pick a start date.</p>
      )}

      {/* Who is being skipped, and why, in the words SKIP_LABELS gives.
          Grouped rather than listed one by one: "3 already in a running round"
          is read at a glance and three separate lines are not. */}
      {plan.skipped.length === 0 ? null : (
        <ul className="round-bulk-skips">
          {plan.skipped.map((group) => (
            <li key={group.reason}>
              <strong>{String(group.leadIds.length)}</strong> skipped — {SKIP_LABELS[group.reason]}
              {group.reason === 'converted-to-deal' ? (
                <span className="round-bulk-skip-names">
                  {' '}
                  ({group.leadIds.map(nameOf).join(', ')})
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* Leads that arrive part-done, named. A person scanning for a mistake
          looks here first: "why does Jacob only owe three" has to be
          answerable without leaving the panel. */}
      {plan.starting.some((entry) => entry.alreadySent > 0) ? (
        <ul className="round-bulk-partial">
          {plan.starting
            .filter((entry) => entry.alreadySent > 0)
            .map((entry) => (
              <li key={entry.leadId}>
                {nameOf(entry.leadId)} starts at {String(entry.alreadySent)} of {String(target)}
              </li>
            ))}
        </ul>
      ) : null}

      <div className="round-bulk-actions">
        <Button
          size="sm"
          disabled={!possible || !hasDate || plan.starting.length === 0 || startRounds.isPending}
          onClick={submit}
        >
          {startRounds.isPending ? 'Starting…' : confirmLabel(plan)}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
